// src/services/venueService.ts
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  addDoc,
  increment
} from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { Venue, VenueFormData, Package, PackageFormData } from '../types/Venue';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Create a new venue
export const createVenue = async (userId: string, venueData: VenueFormData): Promise<string> => {
  try {
    // Create a reference to a new document with an auto-generated ID
    const venueRef = doc(collection(db, 'venues'));
    const venueId = venueRef.id;
    
    // Set default values for the venue
    const newVenue: Omit<Venue, 'id'> = {
      ...venueData,
      ownerId: userId,
      packages: [],
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      images: {
        primary: venueData.images?.primary || '',
        gallery: venueData.images?.gallery || [],
        logo: venueData.images?.logo || ''
      }
    };
    
    // Set the document in Firestore
    await setDoc(venueRef, newVenue);
    
    // Also create an entry in the accounts collection
    await setDoc(doc(db, 'accounts', venueId), {
      type: 'venue',
      userId: userId,
      venueId: venueId,
      createdAt: serverTimestamp(),
    });
    
    return venueId;
  } catch (error) {
    console.error('Error creating venue:', error);
    throw error;
  }
};

// Get venue by ID
export const getVenueById = async (venueId: string): Promise<Venue | null> => {
  try {
    const venueDoc = await getDoc(doc(db, 'venues', venueId));
    
    if (venueDoc.exists()) {
      return { id: venueDoc.id, ...venueDoc.data() } as Venue;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching venue:', error);
    throw error;
  }
};

// Update venue
export const updateVenue = async (venueId: string, venueData: Partial<VenueFormData>): Promise<void> => {
  try {
    const venueRef = doc(db, 'venues', venueId);
    
    // Add updated timestamp
    const updatedData = {
      ...venueData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(venueRef, updatedData);
  } catch (error) {
    console.error('Error updating venue:', error);
    throw error;
  }
};

// Delete venue
export const deleteVenue = async (venueId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'venues', venueId));
    // Also delete from accounts collection
    await deleteDoc(doc(db, 'accounts', venueId));
  } catch (error) {
    console.error('Error deleting venue:', error);
    throw error;
  }
};

// Get all venues owned by a user
export const getVenuesByOwnerId = async (userId: string): Promise<Venue[]> => {
  try {
    const venuesQuery = query(
      collection(db, 'venues'),
      where('ownerId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const venueSnapshot = await getDocs(venuesQuery);
    const venues: Venue[] = [];
    
    venueSnapshot.forEach((doc) => {
      venues.push({ id: doc.id, ...doc.data() } as Venue);
    });
    
    return venues;
  } catch (error) {
    console.error('Error fetching venues:', error);
    throw error;
  }
};

// Upload venue images
export const uploadVenueImage = async (venueId: string, file: File, type: 'primary' | 'logo' | 'gallery'): Promise<string> => {
  try {
    const timestamp = new Date().getTime();
    const storageRef = ref(storage, `venues/${venueId}/${type}_${timestamp}_${file.name}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    // If it's a primary image or logo, update the venue doc directly
    if (type === 'primary' || type === 'logo') {
      await updateDoc(doc(db, 'venues', venueId), {
        [`images.${type}`]: downloadURL,
        updatedAt: serverTimestamp(),
      });
    } else if (type === 'gallery') {
      // For gallery, we need to append to the array
      const venueDoc = await getDoc(doc(db, 'venues', venueId));
      if (venueDoc.exists()) {
        const venueData = venueDoc.data();
        const gallery = venueData.images?.gallery || [];
        
        await updateDoc(doc(db, 'venues', venueId), {
          'images.gallery': [...gallery, downloadURL],
          updatedAt: serverTimestamp(),
        });
      }
    }
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading venue image:', error);
    throw error;
  }
};

// Package Functions
export const createPackage = async (venueId: string, packageData: Omit<Package, 'id'>) => {
  try {
    const packagesRef = collection(db, 'venues', venueId, 'packages');
    
    // Sanitize data before sending to Firestore (remove undefined values)
    const sanitizedData = { ...packageData };
    
    // Handle undefined date values in availability
    if (sanitizedData.availability) {
      const availability = { ...sanitizedData.availability };
      
      // Convert undefined dates to null (Firebase accepts null but not undefined)
      if (availability.startDate === undefined) {
        availability.startDate = null;
      }
      
      if (availability.endDate === undefined) {
        availability.endDate = null;
      }
      
      sanitizedData.availability = availability;
    }
    
    // Handle any other potential undefined values
    Object.keys(sanitizedData).forEach(key => {
      if (sanitizedData[key as keyof typeof sanitizedData] === undefined) {
        (sanitizedData as any)[key] = null;
      }
    });
    
    const newPackageRef = await addDoc(packagesRef, {
      ...sanitizedData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: sanitizedData.isActive || false,
    });
    
    return {
      id: newPackageRef.id,
      ...packageData
    };
  } catch (error) {
    console.error('Error creating package:', error);
    throw error;
  }
};

export const getPackagesByVenueId = async (venueId: string) => {
  try {
    const packagesRef = collection(db, 'venues', venueId, 'packages');
    const q = query(packagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const packages: Package[] = [];
    querySnapshot.forEach((doc) => {
      packages.push({
        id: doc.id,
        ...doc.data()
      } as Package);
    });
    
    return packages;
  } catch (error) {
    console.error('Error getting packages:', error);
    throw error;
  }
};

export const getPackageById = async (venueId: string, packageId: string) => {
  try {
    const packageRef = doc(db, 'venues', venueId, 'packages', packageId);
    const packageDoc = await getDoc(packageRef);
    
    if (packageDoc.exists()) {
      return {
        id: packageDoc.id,
        ...packageDoc.data()
      } as Package;
    } else {
      throw new Error('Package not found');
    }
  } catch (error) {
    console.error('Error getting package:', error);
    throw error;
  }
};

export const updatePackage = async (venueId: string, packageId: string, packageData: Partial<Package>) => {
  try {
    const packageRef = doc(db, 'venues', venueId, 'packages', packageId);
    
    // Remove id from data to be updated
    const { id, ...dataToUpdate } = packageData as any;
    
    await updateDoc(packageRef, {
      ...dataToUpdate,
      updatedAt: serverTimestamp()
    });
    
    return {
      id: packageId,
      ...packageData
    };
  } catch (error) {
    console.error('Error updating package:', error);
    throw error;
  }
};

export const deletePackage = async (venueId: string, packageId: string) => {
  try {
    const packageRef = doc(db, 'venues', venueId, 'packages', packageId);
    await deleteDoc(packageRef);
    return true;
  } catch (error) {
    console.error('Error deleting package:', error);
    throw error;
  }
};

export const uploadPackageImage = async (venueId: string, packageId: string, file: File): Promise<string> => {
  try {
    const timestamp = new Date().getTime();
    const storageRef = ref(storage, `venues/${venueId}/packages/${packageId}/image_${timestamp}_${file.name}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading package image:', error);
    throw error;
  }
};

export const recordPackageView = async (venueId: string, packageId: string): Promise<void> => {
  try {
    const analyticsRef = doc(db, 'venues', venueId, 'packages', packageId, 'analytics', 'views');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (analyticsDoc.exists()) {
      // Update existing analytics
      await updateDoc(analyticsRef, {
        totalViews: increment(1),
        lastViewed: serverTimestamp()
      });
    } else {
      // Create new analytics document
      await setDoc(analyticsRef, {
        totalViews: 1,
        firstViewed: serverTimestamp(),
        lastViewed: serverTimestamp()
      });
    }
    
    // Also update daily views collection for time-series data
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
    
    const dailyViewsRef = doc(db, 'venues', venueId, 'packages', packageId, 'analytics', 'dailyViews');
    const dailyViewsDoc = await getDoc(dailyViewsRef);
    
    if (dailyViewsDoc.exists()) {
      const dailyData = dailyViewsDoc.data();
      const updatedDaily = { 
        ...dailyData,
        [dateKey]: (dailyData[dateKey] || 0) + 1
      };
      await updateDoc(dailyViewsRef, updatedDaily);
    } else {
      await setDoc(dailyViewsRef, { [dateKey]: 1 });
    }
    
  } catch (error) {
    console.error('Error recording package view:', error);
    // Don't throw the error as this is an analytics function and shouldn't block the UI
  }
};

export const getAllPublicPackages = async (limit: number = 20, filterType?: string): Promise<any[]> => {
  try {
    // Get all venues
    const venuesRef = collection(db, 'venues');
    const venuesSnapshot = await getDocs(venuesRef);
    
    // Store all packages here
    const allPackages: any[] = [];
    
    // For each venue, get its packages
    const packagePromises = venuesSnapshot.docs.map(async (venueDoc) => {
      const venueId = venueDoc.id;
      const venueData = venueDoc.data();
      
      const packagesRef = collection(db, 'venues', venueId, 'packages');
      let packagesQuery = query(
        packagesRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      // Add type filter if specified
      if (filterType) {
        packagesQuery = query(
          packagesRef,
          where('isActive', '==', true),
          where('packageType', '==', filterType),
          orderBy('createdAt', 'desc')
        );
      }
      
      const packagesSnapshot = await getDocs(packagesQuery);
      
      // Add each package with its venue info
      packagesSnapshot.docs.forEach(packageDoc => {
        allPackages.push({
          id: packageDoc.id,
          venueId,
          venueName: venueData.name,
          venueType: venueData.type,
          venueLocation: venueData.location,
          ...packageDoc.data()
        });
      });
    });
    
    // Wait for all package queries to complete
    await Promise.all(packagePromises);
    
    // Sort by createdAt descending and apply limit
    allPackages.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    return allPackages.slice(0, limit);
    
  } catch (error) {
    console.error('Error getting public packages:', error);
    throw error;
  }
};
